// @ai-generated 2026-08-24T12:24:35+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { type ApiContainerAction, EnumContainerOrderAction } from '@Model';
import { compareForDisplay } from '@synqo/frontend-component-library';
import { type PlannedContainerGroup } from './containerActionGroups';
import { type ContainerTypeDetailsByTypeId } from './useContainerTypeDetails';

/** A whole collective type as one line: counted, and never carrying a number. */
export interface CollectiveContainerEntry {
    kind: 'collective';
    /** The group's key: a type delivered and collected on one order is two lines, not one. */
    key: string;
    label: string;
    group: PlannedContainerGroup;
}

/** A single container of an individually managed type. */
export interface IndividualContainerEntry {
    kind: 'individual';
    /** The action's id: one line is one planned container. */
    id: string;
    label: string;
    action: ApiContainerAction;
}

/** The pickup and the delivery of one exchange, which belong together on one line. */
export interface ExchangeContainerEntry {
    kind: 'exchange';
    /** The exchange id both rows carry. */
    key: string;
    /** The label of the container picked up. */
    label: string;
    pickup: ApiContainerAction;
    delivery: ApiContainerAction;
}

export type PlannedContainerEntry = CollectiveContainerEntry | IndividualContainerEntry | ExchangeContainerEntry;

/**
 * Pairs the rows of every exchange that has exactly one pickup and one delivery. The remaining rows,
 * including those of an exchange that lost a half, are left to be listed on their own.
 */
// @ai-generated 2026-10-08T19:12:01+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function splitExchanges(actions: readonly ApiContainerAction[]): {
    exchanges: ExchangeContainerEntry[];
    rest: ApiContainerAction[];
} {
    const byExchange = new Map<string, ApiContainerAction[]>();
    actions.forEach((action) => {
        if (action.exchange_id) {
            byExchange.set(action.exchange_id, [...(byExchange.get(action.exchange_id) ?? []), action]);
        }
    });

    const exchanges: ExchangeContainerEntry[] = [];
    const paired = new Set<string>();

    byExchange.forEach((rows, key) => {
        const deliveries = rows.filter((row) => row.action === EnumContainerOrderAction.Place);
        const pickups = rows.filter((row) => row.action !== EnumContainerOrderAction.Place);
        if (deliveries.length !== 1 || pickups.length !== 1) {
            return;
        }

        exchanges.push({
            kind: 'exchange',
            key,
            label: pickups[0].container_type.display_string,
            pickup: pickups[0],
            delivery: deliveries[0],
        });
        rows.forEach((row) => paired.add(row.id));
    });

    return { exchanges, rest: actions.filter((action) => !paired.has(action.id)) };
}

/**
 * The planned containers as one flat list: a line per collective type, a line per container of an
 * individually managed one, and a line per exchange.
 *
 * Ordered by type label so the lines of one type stay together, and in planning order within a type.
 * A type whose record has not arrived yet counts as collective — offering a number before the type is
 * known would take it back again. A row without a container type is a line of its own, waiting for one.
 */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toPlannedContainerEntries(
    groups: readonly PlannedContainerGroup[],
    containerTypes: ContainerTypeDetailsByTypeId,
    exchanges: readonly ExchangeContainerEntry[] = [],
    untyped: readonly ApiContainerAction[] = [],
): PlannedContainerEntry[] {
    const lines = groups.flatMap<PlannedContainerEntry>((group) =>
        containerTypes.get(group.id)?.usesIndividualManagement === true
            ? group.actions.map((action) => ({
                  kind: 'individual',
                  id: action.id,
                  label: group.label,
                  action,
              }))
            : [{ kind: 'collective', key: group.key, label: group.label, group }],
    );
    const waiting = untyped.map<PlannedContainerEntry>((action) => ({
        kind: 'individual',
        id: action.id,
        label: action.container_type.display_string,
        action,
    }));

    return [...lines, ...waiting, ...exchanges].sort((one, other) => compareForDisplay(one.label, other.label));
}
